import {
  FinancialAccountListRaw,
  FinancialAccountList,
  FinancialAccountListItem,
  FinancialAccountDetailsWithBalancesRaw,
  FinancialAccountDetails,
  RequestPageParams,
  FinancialAccountActivityItem,
  FinancialAccountActivitiesRaw,
  FinancialAccountActivities,
  FinancialAccountListRequest,
} from '@shared/models';
import { cleanMaskedAccountNumber, filterCreatedAtDateRange, systemTimeZone, toTitleCase } from '@shared/utils';
import { compact } from 'lodash-es';
import { format, parse, sub } from 'date-fns';
import { constants } from '@shared/constants';
import { HttpParams } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { CustomHttpParamEncoder } from '@shared/encoder';

export const mapFinancialAccountList = (response: FinancialAccountListRaw): FinancialAccountList => {
  const { content = [], totalElements = 0, hasNext } = response || {};

  const items: FinancialAccountListItem[] = content.map((financialAccount) => {
    const verificationStatus = financialAccount.verificationStatus ?? 'UNVERIFIED';
    const accountNumberTail = cleanMaskedAccountNumber(financialAccount.maskedAccountNumber);
    const displayName = financialAccount.bankAccount
      ? `**** ${accountNumberTail} | ${toTitleCase(financialAccount.subtype)} Acc`
      : `**** ${accountNumberTail} | ${toTitleCase(financialAccount.subtype)} Card`;
    const accountBalances = financialAccount._embedded?.balances
      ? financialAccount._embedded?.balances[financialAccount.currency.toUpperCase()]
      : undefined;

    return {
      ...financialAccount,
      financialAccountShortId: financialAccount.id?.split('-')[0].toUpperCase(),
      businessAccountShortId: financialAccount.businessAccountId?.split('-')[0].toUpperCase(),
      lastFourNumbers: accountNumberTail ? `*${accountNumberTail}` : undefined,
      verification: toTitleCase(verificationStatus),
      rowClassName: `verification-${verificationStatus.toLowerCase()}`,
      displayName,
      accountBalance: accountBalances?.accountBalance,
      availableBalance: accountBalances?.availableBalance,
      parentShortId: financialAccount.parentId?.split('-')[0].toUpperCase(),
      role: financialAccount.accountRoles?.join(','),
    };
  });

  return {
    items,
    totalElements,
    hasNext,
  };
};

export const mapFinancialAccountDetails = (financialAccount: FinancialAccountDetailsWithBalancesRaw): FinancialAccountDetails => {
  const accountNumberTail =
    cleanMaskedAccountNumber(financialAccount.maskedAccountNumber) ||
    financialAccount.card?.cardNumberTail ||
    financialAccount.bankAccount?.accountNumberTail ||
    '';
  const accountNumberTailOrNull = accountNumberTail ? `*******${accountNumberTail}` : undefined;
  const bankAccountOrNull = financialAccount.bankAccount ? 'bankAccount' : undefined;
  const embeddedBalances = financialAccount._embedded?.balances ?? undefined;
  const accountBalances = financialAccount._embedded?.balances
    ? financialAccount._embedded?.balances[financialAccount.currency.toUpperCase()]
    : undefined;

  return {
    ...financialAccount,
    financialAccountShortId: financialAccount.id?.split('-')[0].toUpperCase(),
    businessAccountShortId: financialAccount.businessAccountId?.split('-')[0].toUpperCase(),
    parentShortId: financialAccount.parentId?.split('-')[0].toUpperCase(),
    accountHolderShortId: financialAccount.accountHolderId?.split('-')[0].toUpperCase(),
    financialInstitutionShortId: financialAccount.financialInstitutionId?.split('-')[0].toUpperCase(),
    sponsorFinancialAccountShortId: financialAccount.sponsorFinancialAccountId?.split('-')[0].toUpperCase(),
    role: financialAccount.accountRoles?.join(','),
    displayName: financialAccount.bankAccount
      ? `**** ${accountNumberTail} | ${toTitleCase(financialAccount.subtype)} Acc`
      : `**** ${accountNumberTail} | ${toTitleCase(financialAccount.subtype)} Card`,
    lastFourNumbers: financialAccount.card ? `************${accountNumberTail}` : accountNumberTailOrNull,
    card: financialAccount.card
      ? {
          ...financialAccount.card,
          expiry: format(parse(financialAccount.card.expiry, 'yyyy-MM', new Date()), 'MM/yyyy'),
          fullNameOnCard: compact([financialAccount.card.firstName, financialAccount.card.middleName, financialAccount.card.lastName]).join(
            ' ',
          ),
        }
      : undefined,
    cardOrBankAccount: financialAccount.card ? 'card' : bankAccountOrNull,
    accountBalances: accountBalances,
    embeddedAccountBalance: embeddedBalances,
  };
};

export const mapFinancialAccountActivities = (response: FinancialAccountActivitiesRaw): FinancialAccountActivities => {
  const { content = [], totalElements = 0 } = response ?? {};
  const items: FinancialAccountActivityItem[] = content.map((item) => ({
    ...item,
    description: `${item.description ?? ' '}::${item.statementDescription ?? ' '}`,
    status: item.transactionStatus,
    isDisabled: !item.transactionId,
    idWithCreatedAt: `${item.transactionId ?? 'N/A'}::${item.createdAt}`,
  }));

  return {
    items,
    totalElements,
  };
};

export const financialAccountActivitiesRequestParams = (requestParams: RequestPageParams): HttpParams => {
  const { page, size, activeFilters, searchString } = requestParams;

  const pageSize = size ?? constants.TABLE_ROWS;

  let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

  params = params.append('size', pageSize);

  if (searchString) {
    params = params.append('transactionId', searchString);
  }

  if (page) {
    params = params.append('page', page);
  }

  if (activeFilters) {
    Object.keys(activeFilters).forEach((key) => {
      if (key === 'date') {
        const defaultRange = {
          from: format(sub(new Date(), { days: 90 }), 'yyyy-MM-dd'),
          to: format(new Date(), 'yyyy-MM-dd'),
        };
        const { from, to } = filterCreatedAtDateRange(activeFilters[key], defaultRange) ?? {};

        if (!from || !to) {
          return;
        }

        params = params.append('startDateTime', formatDate(from, 'yyyy-MM-ddTHH:mm:ssZZZZZ', 'en'));
        params = params.append('endDateTime', formatDate(`${to} 23:59:59`, 'yyyy-MM-ddTHH:mm:ssZZZZZ', 'en'));
        params = params.append('timeZone', systemTimeZone);
      } else if (key === 'amount') {
        const [min, max] = activeFilters[key]?.split('::') ?? [];

        if (!min || !max) {
          return;
        }

        params = params.append('amountFrom', min);
        params = params.append('amountTo', max);
      } else {
        params = params.append(key, activeFilters[key]!);
      }
    });
  }

  return params;
};

export const financialAccountsRequestParams = (requestParams: FinancialAccountListRequest): HttpParams => {
  const { searchString, sortParams, page, size, parentId, accountHolderTypes = [], activeFilters, businessAccountId } = requestParams;

  const pageSize = size || constants.TABLE_ROWS;

  let params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('page', `${page ?? 0}`).set('size', `${pageSize}`);

  if (businessAccountId) {
    params = params.append('businessAccountId', businessAccountId);
  }

  if (parentId) {
    params = params.append('parentId', parentId);
  }

  if (accountHolderTypes.length) {
    params = params.append('accountHolderTypes', accountHolderTypes.join(','));
  }

  if (searchString) {
    params = params.append('keyword', searchString);
  }

  if (activeFilters) {
    Object.keys(activeFilters).forEach((key) => {
      if (key === 'createdAt') {
        const { from, to } = filterCreatedAtDateRange(activeFilters[key]) ?? {};

        if (!from || !to) {
          return;
        }

        params = params.append('createdDateFrom', from);
        params = params.append('createdDateTo', to);
        params = params.append('timeZone', systemTimeZone);
      } else if (key === 'updatedAt') {
        const { from, to } = filterCreatedAtDateRange(activeFilters[key]) ?? {};

        if (!from || !to) {
          return;
        }

        params = params.append('updatedDateFrom', from);
        params = params.append('updatedDateTo', to);
        params = params.append('timeZone', systemTimeZone);
      } else {
        params = params.append(key, activeFilters[key]!);
      }
    });
  }

  if (sortParams) {
    params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
  }

  return params;
};
